import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import {
  Hotel,
  Add,
  TrendingUp,
  Star,
  Close,
  LocationOn,
  Search,
  Analytics
} from '@mui/icons-material';

import FirestoreService from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import { LoyaltyProgram } from '../types';
import AddProgramDialog from '../components/common/AddProgramDialog';
import HotelProgramDisplay from '../components/common/HotelProgramDisplay';
import HotelCalculationEngine, { BestHotelValue, HotelDeal } from '../services/hotelCalculationEngine';
import HotelAPIService from '../services/hotelAPIService';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));

const DealCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)'
    : 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
  color: 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const HotelCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 8px 25px rgba(0, 0, 0, 0.1)'
      : '0 8px 25px rgba(0, 0, 0, 0.3)',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hotel-tabpanel-${index}`}
      aria-labelledby={`hotel-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const Hotels: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [hotelPrograms, setHotelPrograms] = React.useState<LoyaltyProgram[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [bestHotelValue, setBestHotelValue] = React.useState<BestHotelValue | null>(null);
  const [dynamicDeals, setDynamicDeals] = React.useState<HotelDeal[]>([]);
  const [transferOpportunities, setTransferOpportunities] = React.useState<any[]>([]);
  const [hotelDetailsOpen, setHotelDetailsOpen] = React.useState(false);
  const [selectedDeal, setSelectedDeal] = React.useState<any>(null);
  const [activeTab, setActiveTab] = React.useState(0);

  // Hotel search state
  const [searchDestination, setSearchDestination] = React.useState('');
  const [searchCheckIn, setSearchCheckIn] = React.useState<Date | null>(new Date());
  const [searchCheckOut, setSearchCheckOut] = React.useState<Date | null>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [searchAdults, setSearchAdults] = React.useState(2);
  const [searchRooms, setSearchRooms] = React.useState(1);
  const [searchCurrency, setSearchCurrency] = React.useState('USD');
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [resultsFilter, setResultsFilter] = React.useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [programToDelete, setProgramToDelete] = React.useState<LoyaltyProgram | null>(null);

  const loadDynamicStatistics = React.useCallback(async (programs: LoyaltyProgram[]) => {
    try {
      // Calculate best hotel value
      const bestHotel = await HotelCalculationEngine.calculateBestHotelValue(programs);
      setBestHotelValue(bestHotel);

      // Generate personalized hotel deals
      const deals = await HotelCalculationEngine.generatePersonalizedHotelDeals(programs);
      setDynamicDeals(deals);

      // Get all user's credit cards for transfer opportunities
      const allPrograms = user?.id ? await FirestoreService.getUserLoyaltyPrograms(user.id) : [];
      const creditCards = allPrograms.filter(p => p.type === 'credit_card');
      const transferOpps = HotelCalculationEngine.calculateHotelTransferOpportunities(creditCards, programs);
      setTransferOpportunities(transferOpps);
      
      console.log('✅ Dynamic hotel statistics loaded');
    } catch (error) {
      console.error('Error loading dynamic hotel statistics:', error);
    }
  }, [user]);

  React.useEffect(() => {
    const loadHotelPrograms = async () => {
      try {
        if (!user?.id) {
          setHotelPrograms([]);
          setBestHotelValue(null);
          setDynamicDeals([]);
          setTransferOpportunities([]);
          return;
        }

        const hotelPrograms = await FirestoreService.getUserLoyaltyProgramsByType(user.id, 'hotel');
        setHotelPrograms(hotelPrograms);
        
        await loadDynamicStatistics(hotelPrograms);
      } catch (error) {
        console.error('Error loading hotel programs:', error);
        setHotelPrograms([]);
      } finally {
        if (!authLoading) {
          setLoading(false);
        }
      }
    };

    if (!authLoading) {
      loadHotelPrograms();
    }
  }, [user, authLoading, loadDynamicStatistics]);

  const handleOpenHotelDetails = (deal: any) => {
    setSelectedDeal(deal);
    setHotelDetailsOpen(true);
  };

  const handleCloseHotelDetails = () => {
    setHotelDetailsOpen(false);
    setSelectedDeal(null);
  };

  const handleDeleteHotelProgram = async (programToDelete: LoyaltyProgram) => {
    // Show confirmation dialog
    setProgramToDelete(programToDelete);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!user || !programToDelete) return;
    
    try {
      await FirestoreService.deleteLoyaltyProgram(programToDelete.id);
      setHotelPrograms(prev => prev.filter(program => program.id !== programToDelete.id));
      console.log('Hotel program deleted successfully:', programToDelete.name);
      
      // Close dialog and reset
      setDeleteConfirmOpen(false);
      setProgramToDelete(null);
    } catch (error) {
      console.error('Error deleting hotel program:', error);
      alert(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const HOTEL_DESTINATIONS = HotelAPIService.getHotelDestinations();

  const searchHotels = async () => {
    if (!searchDestination || !searchCheckIn || !searchCheckOut) {
      return;
    }

    setSearchLoading(true);
    try {
      console.log('🏨 Searching hotels...');
      
      const searchParams = {
        destination: searchDestination,
        checkIn: format(searchCheckIn, 'yyyy-MM-dd'),
        checkOut: format(searchCheckOut, 'yyyy-MM-dd'),
        adults: searchAdults,
        rooms: searchRooms,
        currency: searchCurrency
      };

      const results = await HotelAPIService.searchHotels(searchParams);
      setSearchResults(results.hotels || []);
      
      console.log('✅ Hotel search completed:', results.hotels?.length || 0, 'hotels found');
    } catch (error) {
      console.error('❌ Hotel search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const totalPoints = hotelPrograms.reduce((sum: number, program: LoyaltyProgram) => sum + program.points, 0);
  const activePrograms = hotelPrograms.length;

  const handleAddProgram = async (newProgramData: Omit<LoyaltyProgram, 'id'>) => {
    try {
      if (!user?.id) {
        console.error('No user ID available for adding program');
        return;
      }

      const newProgram = await FirestoreService.addLoyaltyProgram(user.id, newProgramData);
      setHotelPrograms(prev => [...prev, newProgram]);
      
      console.log('Hotel program added successfully:', newProgram);
    } catch (error) {
      console.error('Error adding hotel program:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
          Hotel Points Tracker
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
          Manage your hotel loyalty programs and discover premium stays
        </Typography>

        {/* Summary Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4 
        }}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Hotel sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {totalPoints.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Hotel Points
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Across all programs
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Star sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {activePrograms}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Programs
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Connected accounts
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <TrendingUp sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {bestHotelValue?.estimatedValue ? `$${bestHotelValue.estimatedValue.toFixed(0)}` : '$0'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Portfolio Value
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Estimated worth
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard 
            sx={{ 
              background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => theme.shadows[8]
              }
            }}
            onClick={() => setAddDialogOpen(true)}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Add sx={{ fontSize: 48, color: 'white', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Add Program
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Connect new hotel
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }} display="block">
                Track more points
              </Typography>
            </CardContent>
          </StyledCard>
        </Box>
      </Box>

      {/* Main Content Tabs */}
      <Box sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab 
            label="My Programs" 
            icon={<Analytics />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
          <Tab 
            label="Search Hotels" 
            icon={<Search />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
          <Tab 
            label="Deals & Offers" 
            icon={<Star />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
          <Tab 
            label="Analytics" 
            icon={<TrendingUp />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600 }} 
          />
        </Tabs>

        {/* Tab 1: My Programs */}
        <TabPanel value={activeTab} index={0}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Your Hotel Programs
            </Typography>
          </Box>

          {hotelPrograms.length === 0 ? (
            <Alert severity="info">
              No hotel programs found. Add your first hotel loyalty program to get started!
            </Alert>
          ) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(3, 1fr)', 
                lg: 'repeat(4, 1fr)' 
              },
              gap: 2,
              mb: 4
            }}>
              {hotelPrograms.map((program) => (
                <HotelProgramDisplay
                  key={program.id}
                  program={program}
                  onDelete={handleDeleteHotelProgram}
                />
              ))}
            </Box>
          )}
        </TabPanel>

        {/* Tab 2: Search Hotels */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(6, 1fr) auto' },
            gap: 3,
            mb: 4 
          }}>
            <Autocomplete
              options={HOTEL_DESTINATIONS}
              getOptionLabel={(option) => `${option.name}, ${option.country}`}
              value={HOTEL_DESTINATIONS.find(dest => dest.name === searchDestination) || null}
              onChange={(_, newValue) => setSearchDestination(newValue?.name || '')}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Destination" 
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <LocationOn sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
              )}
            />
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Check-in"
                value={searchCheckIn}
                onChange={(newValue) => setSearchCheckIn(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Check-out"
                value={searchCheckOut}
                onChange={(newValue) => setSearchCheckOut(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel>Adults</InputLabel>
              <Select
                value={searchAdults}
                label="Adults"
                onChange={(e) => setSearchAdults(Number(e.target.value))}
              >
                {[1, 2, 3, 4].map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Rooms</InputLabel>
              <Select
                value={searchRooms}
                label="Rooms"
                onChange={(e) => setSearchRooms(Number(e.target.value))}
              >
                {[1, 2, 3, 4].map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              sx={{ height: 56 }}
              onClick={searchHotels}
              disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={20} /> : <Search />}
            >
              {searchLoading ? '' : 'Search'}
            </Button>
          </Box>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {/* Results Filter Bar */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Filter results by hotel name, location, or amenities..."
                  value={resultsFilter}
                  onChange={(e) => setResultsFilter(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'background.paper',
                    }
                  }}
                />
                {resultsFilter && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Showing {searchResults.filter(hotel => 
                      hotel.name.toLowerCase().includes(resultsFilter.toLowerCase()) ||
                      hotel.address.toLowerCase().includes(resultsFilter.toLowerCase()) ||
                      hotel.amenities.some((amenity: string) => amenity.toLowerCase().includes(resultsFilter.toLowerCase()))
                    ).length} of {searchResults.length} results
                  </Typography>
                )}
              </Box>

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 3 
              }}>
                {searchResults
                  .filter(hotel => 
                    !resultsFilter || 
                    hotel.name.toLowerCase().includes(resultsFilter.toLowerCase()) ||
                    hotel.address.toLowerCase().includes(resultsFilter.toLowerCase()) ||
                    hotel.amenities.some((amenity: string) => amenity.toLowerCase().includes(resultsFilter.toLowerCase()))
                  )
                  .map((hotel) => (
                <HotelCard key={hotel.id}>
                  <Box
                    sx={{
                      height: 200,
                      backgroundImage: `url(${hotel.images[0]})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative'
                    }}
                  >
                    <Chip
                      label={`${hotel.rating} ★`}
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white'
                      }}
                    />
                  </Box>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {hotel.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {hotel.address}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {hotel.price.currency} {hotel.price.amount}
                      </Typography>
                      {hotel.pointsRequired && (
                        <Chip 
                          label={`${hotel.pointsRequired.toLocaleString()} points`}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {hotel.amenities.slice(0, 3).map((amenity: string) => (
                        <Chip key={amenity} label={amenity} size="small" variant="outlined" />
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleOpenHotelDetails(hotel)}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </HotelCard>
              ))}
            </Box>
            </Box>
          )}
        </TabPanel>

        {/* Tab 3: Deals & Offers */}
        <TabPanel value={activeTab} index={2}>
          {dynamicDeals.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3 
            }}>
              {dynamicDeals.map((deal) => (
                <DealCard key={deal.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        {deal.title}
                      </Typography>
                      <Chip 
                        label={`Save $${deal.savings}`} 
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                      {deal.description}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {deal.hotelChain}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Valid until {deal.validUntil}
                      </Typography>
                    </Box>

                    <Button 
                      variant="contained" 
                      fullWidth 
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                      }}
                      onClick={() => handleOpenHotelDetails(deal)}
                    >
                      View Deal
                    </Button>
                  </CardContent>
                </DealCard>
              ))}
            </Box>
          ) : (
            <Alert severity="info">
              Add hotel loyalty programs to see personalized deals and offers!
            </Alert>
          )}
        </TabPanel>

        {/* Tab 4: Analytics */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3 
          }}>
            {bestHotelValue && (
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Best Hotel Program
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {bestHotelValue.programName}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {bestHotelValue.hotelChain}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {bestHotelValue.recommendation}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">
                      {bestHotelValue.pointsPerDollar} pts/$
                    </Typography>
                    <Typography variant="caption">
                      ${bestHotelValue.estimatedValue}
                    </Typography>
                  </Box>
                </CardContent>
              </StyledCard>
            )}

            {transferOpportunities.length > 0 && (
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Transfer Opportunities
                  </Typography>
                  {transferOpportunities.slice(0, 3).map((opportunity, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {opportunity.fromProgram} → {opportunity.toHotelProgram}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opportunity.transferRatio} • Value: ${opportunity.potentialValue}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </StyledCard>
            )}
          </Box>
        </TabPanel>
      </Box>

      {/* Add Program Dialog */}
      <AddProgramDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddProgram}
        programType="hotel"
      />

      {/* Hotel Details Dialog */}
      <Dialog
        open={hotelDetailsOpen}
        onClose={handleCloseHotelDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Hotel Details</Typography>
          <IconButton onClick={handleCloseHotelDetails}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDeal && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedDeal.name || selectedDeal.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedDeal.description}
              </Typography>
              {selectedDeal.price && (
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {selectedDeal.price.currency} {selectedDeal.price.amount}
                </Typography>
              )}
              {selectedDeal.amenities && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDeal.amenities.map((amenity: string) => (
                    <Chip key={amenity} label={amenity} variant="outlined" />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHotelDetails}>Close</Button>
          <Button variant="contained">Book Now</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: (theme) => theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          Delete Hotel Program?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{programToDelete?.name}</strong>? 
            This action cannot be undone and will permanently remove the program and all associated data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setProgramToDelete(null);
            }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained" 
            color="error"
            sx={{ ml: 1 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};